import { gameStorage } from "./gameIdentity.js";
/* global globalThis */
import { collectionFailureMessage } from "./listenerContribution.js";
export const COLLECTION_API_ORIGIN = "https://toecm-purepolyglot.hf.space";
const COLLECTION_TIMEOUT_MS = 12000;
const DEFAULT_APP_ID = "PureVersation";
const DEFAULT_PARTICIPANT_NAMESPACE = "pureversation";
const CONSENT_STORAGE_KEY = "pure_collection_consent_receipts_v3";
const PENDING_CONSENT_KEY = "pure_collection_pending_consents_v3";
const DELETION_STORAGE_KEY = "pure_collection_deletion_receipts_v3";
const WITHDRAWN_STORAGE_KEY = "pure_collection_withdrawn_participants_v3";
const PARTICIPANT_TOKEN_STORAGE_KEY = "pure_collection_participant_tokens_v1";
const consentRequests = new Map();
const withdrawalRequests = new Set();
const consentRequestGenerations = new Map();
const CONVERSATION_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const OPERATIONAL_CONTENT_PREFIXES = Object.freeze([
    "archivist_mission_", "speed_stage_", "speed_mission_", "vision_mission_",
    "echo_memory_", "bridge_p1_lang_", "bridge_p2_lang_", "bridge_p1_", "bridge_p2_",
    "bridge_log_", "phc_phase_", "phc_myLang_", "phc_myD_", "phc_active_rooms_",
    "phc_current_room_", "phc_room_", "phc_chat_", "phc_partnerD_",
]);

export const COLLECTION_CONSENT_VERSION = "pure-ethical-crowdsourcing-2026-10-06-session-audio";
export const COLLECTION_PRIVACY_NOTICE_VERSION = "pure-privacy-session-audio-2026-10-06";
export const COLLECTION_RETENTION_POLICY_VERSION = "pure-retention-private-audio-2026-10-06";
export const COLLECTION_WITHDRAWAL_POLICY_VERSION = "pure-withdrawal-v2-2026-10-06";
export const COLLECTION_DATASET_LICENSE = "CC-BY-4.0";
export const COLLECTION_CONSENT_SURFACE = "ethical-ai-consent-screen";
export const COLLECTION_RESEARCH_PURPOSES = Object.freeze([
    "inclusive-language-and-dialect-research",
    "translation-and-conversation-model-evaluation",
    "human-review-and-dataset-quality",
]);
export const COLLECTION_DATA_RECIPIENT_CATEGORIES = Object.freeze([
    "project-research-team",
    "approved-human-reviewers",
    "configured-ai-and-hosting-service-providers",
    "public-dataset-users",
    "researchers-and-ai-developers",
]);
export const COLLECTION_PERMITTED_MODALITIES = Object.freeze([
    "text", "speech", "multimodal", "judgment",
]);
export const COLLECTION_DEFAULT_CONTROLLER_NAME = "PureVersation Research Project (toecm)";
export const COLLECTION_DEFAULT_CONTROLLER_CONTACT = "https://huggingface.co/toecm";
export const COLLECTION_DEFAULT_CONSENT_TEXT_SHA256 = "c8dcef89b29f494efc95d25e328fd3fa78b953d0c4c603899ecf0e1882d54764";

const CONSENT_FIELDS = [
    "participant_id", "app_id", "app_version", "consent_version", "privacy_notice_version",
    "dataset_license", "permitted_modalities", "consent_granted", "consent_surface",
    "consent_locale", "consent_text_sha256", "research_purposes", "retention_policy_version",
    "withdrawal_policy_version", "eligibility_confirmed", "raw_audio_consent",
    "public_release_confirmed", "commercial_ai_reuse_confirmed",
    "privacy_risks_acknowledged", "operational_processing_acknowledged",
    "voluntary_participation_confirmed", "contribution_rights_confirmed",
    "data_controller_name", "data_controller_contact", "data_recipient_categories",
];
const REQUIRED_ACKNOWLEDGMENT_FIELDS = [
    "eligibility_confirmed", "public_release_confirmed",
    "commercial_ai_reuse_confirmed", "privacy_risks_acknowledged",
    "operational_processing_acknowledged", "voluntary_participation_confirmed",
    "contribution_rights_confirmed",
];
const CONSENT_REQUEST_FIELDS = [...CONSENT_FIELDS, "withdrawal_token_sha256"];
const CONSENT_RECEIPT_FIELDS = [
    "consent_record_sha256", "receipt_schema_version", "consent_status", "consent_provenance",
];
const SUBMISSION_FIELDS = [
    "participant_id", "consent_id", "app_id", "app_version", "game_id", "task_id",
    "prompt_text", "topic_id", "image_asset_id", "conversation_id", "turn_id",
    "preceding_context", "source_text", "source_language", "source_dialect",
    "target_language", "target_dialect", "input_modality", "machine_transcript_initial",
    "user_transcript_final", "machine_output_initial", "user_output_final", "model_name",
    "model_version", "asr_model", "translation_route", "metadata", "started_at", "duration_ms",
];
const JUDGMENT_FIELDS = [
    "participant_id", "consent_id", "submission_id", "task_id", "choice", "error_span",
    "error_type", "correction", "confidence", "latency_ms",
];
const CONSENT_ID_PATTERN = /^cns_[0-9a-f]{32}$/;
const SUBMISSION_ID_PATTERN = /^sub_[0-9a-f]{32}$/;
const SHA256_PATTERN = /^[0-9a-f]{64}$/;
const PARTICIPANT_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43,128}$/;

const storage = () => {
    try { return globalThis.localStorage || null; } catch { return null; }
};

export const clearGameStoragePreservingCollection = () => {
    const target = storage();
    if (!target) return;
    const removableKeys = [];
    for (let index = 0; index < target.length; index += 1) {
        const key = target.key(index);
        if (key && !key.startsWith("pure_collection_")) removableKeys.push(key);
    }
    removableKeys.forEach(key => target.removeItem(key));
};

export const purgeLegacyEmbeddedWalletStorage = () => {
    const target = storage();
    if (!target) return;
    const removableKeys = [];
    for (let index = 0; index < target.length; index += 1) {
        const key = target.key(index);
        if (key?.startsWith("pure_web3_")) removableKeys.push(key);
    }
    removableKeys.forEach(key => target.removeItem(key));
    if (target.getItem("pure_identity_type") === "web3") {
        target.removeItem("pure_identity_type");
        target.removeItem("pureversation_operator_addr");
    }
};

export const purgeLegacyTelemetryStorage = () => {
    const target = storage();
    if (!target) return;
    const prefixes = ["pure_passive_eeqs", "pure_manual_eeqs", "pure_eeqs"];
    const removableKeys = [];
    for (let index = 0; index < target.length; index += 1) {
        const key = target.key(index);
        if (key && prefixes.some(prefix => key.startsWith(prefix))) removableKeys.push(key);
    }
    removableKeys.forEach(key => target.removeItem(key));
};

export const clearUserOperationalStorage = (userKey = "") => {
    const target = storage();
    if (!target) return;
    const normalized = String(userKey || "");
    const removableKeys = [];
    for (let index = 0; index < target.length; index += 1) {
        const key = target.key(index);
        if (!key) continue;
        if (key === "pure_nickname" || OPERATIONAL_CONTENT_PREFIXES.some(prefix => (
            key.startsWith(prefix) && (!normalized || key.slice(prefix.length).startsWith(normalized))
        ))) removableKeys.push(key);
    }
    removableKeys.forEach(key => target.removeItem(key));
};

export const writeExpiringConversationState = (key, value, now = Date.now()) => {
    const target = gameStorage;
    if (!target || !key) return false;
    try {
        target.setItem(key, JSON.stringify({
            saved_at: new Date(now).toISOString(),
            expires_at: new Date(now + CONVERSATION_CACHE_TTL_MS).toISOString(),
            value,
        }));
        return true;
    } catch { return false; }
};

export const readExpiringConversationState = (key, now = Date.now()) => {
    const target = gameStorage;
    if (!target || !key) return null;
    try {
        const envelope = JSON.parse(target.getItem(key) || "null");
        const expiresAt = Date.parse(envelope?.expires_at || "");
        if (!envelope || !("value" in envelope) || !Number.isFinite(expiresAt) || expiresAt <= now) {
            target.removeItem(key);
            return null;
        }
        return envelope.value;
    } catch {
        target.removeItem(key);
        return null;
    }
};

const readObject = (key) => {
    try {
        const value = JSON.parse(storage()?.getItem(key) || "{}");
        return value && typeof value === "object" && !Array.isArray(value) ? value : {};
    } catch {
        return {};
    }
};

const writeObject = (key, value) => {
    try { storage()?.setItem(key, JSON.stringify(value)); } catch { /* Storage is best-effort. */ }
};

const pickFields = (payload, fields) => Object.fromEntries(
    fields.filter(field => payload[field] !== undefined).map(field => [field, payload[field]])
);

const createId = (prefix) => {
    const randomId = globalThis.crypto?.randomUUID?.()
        || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    return `${prefix}_${randomId}`;
};

export const createCollectionId = createId;

export const buildCollectionConsentManifest = ({
    dataControllerName = COLLECTION_DEFAULT_CONTROLLER_NAME,
    dataControllerContact = COLLECTION_DEFAULT_CONTROLLER_CONTACT,
} = {}) => ({
    consent_version: COLLECTION_CONSENT_VERSION,
    privacy_notice_version: COLLECTION_PRIVACY_NOTICE_VERSION,
    retention_policy_version: COLLECTION_RETENTION_POLICY_VERSION,
    withdrawal_policy_version: COLLECTION_WITHDRAWAL_POLICY_VERSION,
    dataset_license: COLLECTION_DATASET_LICENSE,
    consent_surface: COLLECTION_CONSENT_SURFACE,
    research_purposes: [...COLLECTION_RESEARCH_PURPOSES],
    data_recipient_categories: [...COLLECTION_DATA_RECIPIENT_CATEGORIES],
    notice: {
        controller: `${dataControllerName} (${dataControllerContact})`,
        questions_and_complaints: "Use the controller contact above for questions or complaints about the research, privacy, withdrawal, or participant rights.",
        voluntary: "Participation and every contribution are voluntary. You may choose browse-only mode and play without contributing, and declining has no penalty.",
        duration_benefits_and_alternative: "Each optional contribution normally takes only the time needed to review and submit one game turn. There is no direct benefit or compensation; the hoped-for benefit is improved inclusive language research and tools. You may instead play without contributing.",
        purposes: "Reviewed contributions are collected for inclusive language and dialect research, translation and conversation model evaluation, and human review/dataset quality.",
        recipients: "Recipients may include the project research team, approved human reviewers, configured AI and hosting service providers, public dataset users, and researchers and AI developers.",
        collected_data: "Depending on your choices, the project collects reviewed text or transcripts, dialect and language labels, annotations, task context, edits, and peer-review judgments, plus a random revocable participant ID and consent receipt. It does not ask for your name, email, or wallet as a participant identifier.",
        processing: "To run selected AI features, game inputs may be transmitted to configured AI and hosting service providers and may be retained or processed internationally under those providers' terms. This operational processing is distinct from research collection: browse-only mode prevents the input from becoming a research contribution, but it does not prevent the processing required to provide an AI feature you choose to use. Automated systems may transcribe or translate an input, but only the human-reviewed contribution is eligible for the research queue. Contributions are quarantined for moderation before release.",
        device_local_storage: "A persistent game pseudonym and saved gameplay are reused only with verified research consent. Browse-only play uses temporary in-memory game identities and state. Separate participant IDs, consent receipts and private withdrawal credentials are retained to authenticate deletion requests and follow-up, not to identify browse-only gameplay. Conversation state expires after 24 hours and is cleared on successful withdrawal or game-data reset. Do not use a shared device for sensitive material.",
        ephemeral_live_chat: "Live matchmaking messages are stored only in ephemeral server memory to deliver the conversation. They become inaccessible after one hour and are periodically purged; they are not research contributions unless you separately submit reviewed text.",
        license_and_publication: "Accepted contributions may be published and reused worldwide under CC BY 4.0. This license permits commercial reuse as well as AI research and development, with attribution. Public or downstream copies already obtained by others may not be fully retractable.",
        privacy_and_risks: "Do not provide personal, confidential, copyrighted, or sensitive information about yourself or anyone else. Text and voice can reveal identity, community, location, or sensitive traits even without a name.",
        audio: "Speech permission covers processing to create a reviewed transcript, not recording storage. Optional private recording retention requires a separate affirmative voice-privacy acknowledgment and server receipt under the displayed recording policy, followed by confirmation before each game and after returning from closing, minimizing or backgrounding the app. That session confirmation authorizes automatic storage of completed recordings started with a game microphone, without individual Save prompts; it does not activate the microphone. Session permission lasts at most 12 hours. The recording policy states its retention period. Raw recordings are not publicly released or licensed under the text dataset license; identity recognition, voice cloning and model training are not authorized by this option.",
        retention: "Text and judgment records follow the request-based withdrawal policy; canonical audit records are tombstoned, not represented as fully erased. Optional private recordings have a separately disclosed retention deadline, expiry cleanup and erasure of active payloads on withdrawal. Backup erasure is tracked separately and is not automatically attested.",
        withdrawal: "You can submit a tracked withdrawal/deletion request from Data & consent as easily as you consented. Self-service withdrawal requires your pseudonymous participant ID and the private credential stored by the app; the ID alone cannot authorize deletion. If that credential is unavailable, contacting the controller can begin a manual verification process. The response distinguishes controller-held tombstoning from pending propagation to public exports and cannot promise erasure of downstream CC BY 4.0 copies.",
        eligibility: "Contributors must be at least 18 (or the age of legal consent where they live), be authorized to contribute the material, and avoid third-party personal data.",
        compensation: "This consent does not promise payment, authorship, or individual attribution for accepted contributions.",
    },
});

export const buildCollectionConsentText = (controller = {}) => JSON.stringify(
    buildCollectionConsentManifest(controller)
);

export const sha256CollectionConsentText = async (text) => {
    if (!globalThis.crypto?.subtle || typeof TextEncoder === "undefined") {
        throw new Error("Secure consent hashing is unavailable in this browser");
    }
    const bytes = new TextEncoder().encode(String(text));
    const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
};

const PARTICIPANT_ID_PATTERN = /^participant_[0-9a-f]{32}$/;

const createSecureParticipantId = () => {
    if (!globalThis.crypto?.getRandomValues) return "";
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    return `participant_${Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("")}`;
};

export const getCollectionParticipantId = (_candidate, appId = DEFAULT_PARTICIPANT_NAMESPACE, { create = false } = {}) => {
    const storageKey = `pure_collection_participant_${appId}_v2`;
    try {
        const stored = storage()?.getItem(storageKey);
        if (create && withdrawalRequests.has(stored)) return "";
        if (stored && PARTICIPANT_ID_PATTERN.test(stored) && (!create || !isWithdrawn(stored))) return stored;
        if (!create) return "";
        const participantId = createSecureParticipantId();
        if (!participantId) return "";
        storage()?.setItem(storageKey, participantId);
        return storage()?.getItem(storageKey) === participantId ? participantId : "";
    } catch {
        return "";
    }
};

const consentRequestKey = (participantId, appId) => `${appId}:${participantId}`;
const advanceConsentRequestGeneration = (requestKey) => {
    const generation = (consentRequestGenerations.get(requestKey) || 0) + 1;
    consentRequestGenerations.set(requestKey, generation);
    return generation;
};
const isCurrentConsentRequest = (requestKey, generation) => (
    consentRequestGenerations.get(requestKey) === generation
);

const generateParticipantToken = () => {
    if (!globalThis.crypto?.getRandomValues || typeof globalThis.btoa !== "function") {
        throw new Error("Secure participant credential generation is unavailable in this browser");
    }
    const bytes = new Uint8Array(32);
    globalThis.crypto.getRandomValues(bytes);
    const binary = Array.from(bytes, byte => String.fromCharCode(byte)).join("");
    return globalThis.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const getParticipantToken = (participantId, appId, { create = false } = {}) => {
    const requestKey = consentRequestKey(participantId, appId);
    const credentials = readObject(PARTICIPANT_TOKEN_STORAGE_KEY);
    const existing = String(credentials[requestKey] || "");
    if (PARTICIPANT_TOKEN_PATTERN.test(existing)) return existing;
    if (!create) return "";
    const token = generateParticipantToken();
    credentials[requestKey] = token;
    writeObject(PARTICIPANT_TOKEN_STORAGE_KEY, credentials);
    if (readObject(PARTICIPANT_TOKEN_STORAGE_KEY)[requestKey] !== token) {
        throw new Error("The private withdrawal credential could not be saved. Enable browser storage before consenting.");
    }
    return token;
};

const clearParticipantToken = (participantId, appId) => {
    const requestKey = consentRequestKey(participantId, appId);
    const credentials = readObject(PARTICIPANT_TOKEN_STORAGE_KEY);
    delete credentials[requestKey];
    writeObject(PARTICIPANT_TOKEN_STORAGE_KEY, credentials);
};

const getConsentReceipt = (participantId, appId = DEFAULT_APP_ID) => (
    readObject(CONSENT_STORAGE_KEY)[consentRequestKey(participantId, appId)] || null
);

const isVerifiedReceipt = (receipt, { participantId, appId, consentVersion, modality } = {}) => {
    if (!receipt || !CONSENT_ID_PATTERN.test(String(receipt.consent_id || ""))) return false;
    if (participantId && receipt.participant_id !== participantId) return false;
    if (appId && receipt.app_id !== appId) return false;
    if (consentVersion && receipt.consent_version !== consentVersion) return false;
    if (receipt.consent_version !== COLLECTION_CONSENT_VERSION) return false;
    if (receipt.raw_audio_consent !== false
        || REQUIRED_ACKNOWLEDGMENT_FIELDS.some(field => receipt[field] !== true)) return false;
    if (modality && !receipt.permitted_modalities?.includes(modality)) return false;
    return true;
};

export const getCollectionConsentId = (participantId, appId = DEFAULT_APP_ID) => {
    if (withdrawalRequests.has(participantId) || isWithdrawn(participantId)) return "";
    const receipt = getConsentReceipt(participantId, appId);
    return isVerifiedReceipt(receipt, { participantId, appId })
        && getParticipantToken(participantId, appId)
        ? receipt.consent_id
        : "";
};

export const getCollectionConsentProfile = (participantId, appId = DEFAULT_APP_ID) => {
    const receipt = getConsentReceipt(participantId, appId);
    if (!isVerifiedReceipt(receipt, { participantId, appId })
        || !getParticipantToken(participantId, appId)) return null;
    return {
        ...receipt,
        permitted_modalities: [...(receipt.permitted_modalities || [])],
    };
};

export const hasVerifiedCollectionConsent = (participantId, appId = DEFAULT_APP_ID, modality) => (
    !withdrawalRequests.has(participantId) && !isWithdrawn(participantId) && isVerifiedReceipt(getConsentReceipt(participantId, appId), {
        participantId, appId, consentVersion: COLLECTION_CONSENT_VERSION, modality,
    }) && Boolean(getParticipantToken(participantId, appId))
);

export const canEnterCollectionExperience = ({ participantId, appId = DEFAULT_APP_ID, mode = "" }) => (
    mode === "browse-only" || hasVerifiedCollectionConsent(participantId, appId)
);

const persistConsentReceipt = (payload, receipt) => {
    const receipts = readObject(CONSENT_STORAGE_KEY);
    receipts[consentRequestKey(payload.participant_id, payload.app_id)] = {
        ...pickFields(payload, CONSENT_FIELDS),
        ...pickFields(receipt, CONSENT_RECEIPT_FIELDS),
        consent_id: receipt.consent_id,
        recorded_at: receipt.recorded_at || new Date().toISOString(),
    };
    writeObject(CONSENT_STORAGE_KEY, receipts);
};

const persistPendingConsent = (payload, idempotencyKey, error = "") => {
    const pending = readObject(PENDING_CONSENT_KEY);
    pending[consentRequestKey(payload.participant_id, payload.app_id)] = {
        payload: pickFields(payload, CONSENT_FIELDS),
        idempotency_key: idempotencyKey,
        last_error: error,
    };
    writeObject(PENDING_CONSENT_KEY, pending);
};

const clearPendingConsent = (participantId, appId) => {
    const pending = readObject(PENDING_CONSENT_KEY);
    delete pending[consentRequestKey(participantId, appId)];
    writeObject(PENDING_CONSENT_KEY, pending);
};

const isWithdrawn = (participantId) => Boolean(readObject(WITHDRAWN_STORAGE_KEY)[participantId]);

const collectionEndpoints = (resource) => {
    const path = `/api/v2/${resource}`;
    return [`${COLLECTION_API_ORIGIN}${path}`];
};

const requestJson = async (url, { method = "POST", payload, idempotencyKey, participantToken }) => {
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timeout = controller ? setTimeout(() => controller.abort(), COLLECTION_TIMEOUT_MS) : null;
    try {
        const response = await fetch(url, {
            method,
            headers: {
                "Content-Type": "application/json",
                ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
                ...(participantToken ? { "X-Participant-Token": participantToken } : {}),
            },
            ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
            ...(controller ? { signal: controller.signal } : {}),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            const error = new Error(collectionFailureMessage(response.status, data.detail || data.error));
            error.collectionStatus = response.status;
            throw error;
        }
        return data;
    } finally {
        if (timeout) clearTimeout(timeout);
    }
};

export const getCurrentCollectionConsentPolicy = async (consentLocale = "en") => {
    const resource = `consent-policies/current?consent_locale=${encodeURIComponent(consentLocale)}`;
    const errors = [];
    for (const endpoint of collectionEndpoints(resource)) {
        try {
            const policy = await requestJson(endpoint, { method: "GET" });
            const calculatedHash = await sha256CollectionConsentText(policy.canonical_text || "");
            if (calculatedHash !== policy.consent_text_sha256) {
                throw new Error("The policy registry returned a consent statement with an invalid hash");
            }
            return policy;
        } catch (error) {
            errors.push(error);
        }
    }
    const combinedError = new Error("The current consent policy could not be verified");
    combinedError.causes = errors;
    throw combinedError;
};

const sameStringArray = (left, right) => (
    Array.isArray(left) && Array.isArray(right)
    && left.length === right.length
    && left.every((value, index) => value === right[index])
);

const verifyRegisteredPolicy = async (payload) => {
    const policy = await getCurrentCollectionConsentPolicy(payload.consent_locale);
    const bundledText = buildCollectionConsentText({
        dataControllerName: payload.data_controller_name,
        dataControllerContact: payload.data_controller_contact,
    });
    const exactFieldsMatch = (
        policy.canonical_text === bundledText
        && policy.consent_text_sha256 === payload.consent_text_sha256
        && policy.consent_version === payload.consent_version
        && policy.privacy_notice_version === payload.privacy_notice_version
        && policy.retention_policy_version === payload.retention_policy_version
        && policy.withdrawal_policy_version === payload.withdrawal_policy_version
        && policy.dataset_license === payload.dataset_license
        && policy.consent_surface === payload.consent_surface
        && policy.data_controller_name === payload.data_controller_name
        && policy.data_controller_contact === payload.data_controller_contact
        && sameStringArray(policy.research_purposes, payload.research_purposes)
        && sameStringArray(policy.data_recipient_categories, payload.data_recipient_categories)
        && payload.permitted_modalities.every(modality => policy.allowed_modalities?.includes(modality))
    );
    if (!exactFieldsMatch) {
        throw new Error("The displayed consent notice does not match the registered current policy");
    }
    return policy;
};

const sendToCentral = async (resource, payload, idempotencyKey, method = "POST", participantToken = "") => {
    if (method !== "GET" && !PARTICIPANT_TOKEN_PATTERN.test(String(participantToken))) {
        throw new Error("A client-held participant credential is required for collection mutations");
    }
    const errors = [];
    for (const endpoint of collectionEndpoints(resource)) {
        try {
            const data = await requestJson(endpoint, { method, payload, idempotencyKey, participantToken });
            return { transport: "v2", endpoint, data };
        } catch (error) {
            errors.push(error);
        }
    }
    const combinedError = new Error(errors[0]?.message || `Central ${resource} collection is unavailable`);
    combinedError.collectionStatus = errors[0]?.collectionStatus || 0;
    combinedError.causes = errors;
    throw combinedError;
};

const validateConsentPayload = (payload) => {
    if (!PARTICIPANT_ID_PATTERN.test(String(payload.participant_id || "")) || !payload.app_id || !payload.app_version) {
        throw new Error("Consent provenance is incomplete");
    }
    if (payload.consent_version !== COLLECTION_CONSENT_VERSION
        || payload.privacy_notice_version !== COLLECTION_PRIVACY_NOTICE_VERSION
        || payload.retention_policy_version !== COLLECTION_RETENTION_POLICY_VERSION
        || payload.withdrawal_policy_version !== COLLECTION_WITHDRAWAL_POLICY_VERSION) {
        throw new Error("Consent policy versions do not match the current policy bundle");
    }
    if (!SHA256_PATTERN.test(String(payload.consent_text_sha256 || ""))) {
        throw new Error("The canonical consent notice hash is missing or invalid");
    }
    if (!SHA256_PATTERN.test(String(payload.withdrawal_token_sha256 || ""))) {
        throw new Error("The participant withdrawal credential hash is missing or invalid");
    }
    if (!Array.isArray(payload.permitted_modalities) || payload.permitted_modalities.length === 0) {
        throw new Error("At least one contribution modality must be selected");
    }
    if (payload.consent_granted !== true
        || REQUIRED_ACKNOWLEDGMENT_FIELDS.some(field => payload[field] !== true)) {
        throw new Error("Every required consent acknowledgment must be actively confirmed");
    }
    if (payload.raw_audio_consent !== false) {
        throw new Error("Raw-audio consent must remain false for transcript-first collection");
    }
};

export const submitConsentEvent = (payload, { idempotencyKey } = {}) => {
    const appId = payload.app_id || DEFAULT_APP_ID;
    const persistedPayload = pickFields({ ...payload, app_id: appId }, CONSENT_FIELDS);
    if (!PARTICIPANT_ID_PATTERN.test(String(persistedPayload.participant_id || ""))) {
        return Promise.reject(new Error("A secure pseudonymous participant ID is required before consenting"));
    }
    if (isWithdrawn(persistedPayload.participant_id)) {
        return Promise.reject(new Error("This participant has withdrawn; new collection is disabled"));
    }

    const requestKey = consentRequestKey(persistedPayload.participant_id, appId);
    if (withdrawalRequests.has(persistedPayload.participant_id)) return Promise.reject(new Error("Wait for the withdrawal request before consenting again."));
    const requestGeneration = advanceConsentRequestGeneration(requestKey);
    const key = idempotencyKey || createId("consent");
    const participantToken = getParticipantToken(persistedPayload.participant_id, appId, { create: true });
    persistPendingConsent(persistedPayload, key);
    let request;
    request = (async () => {
        try {
            const normalizedPayload = pickFields({
                ...persistedPayload,
                withdrawal_token_sha256: await sha256CollectionConsentText(participantToken),
            }, CONSENT_REQUEST_FIELDS);
            validateConsentPayload(normalizedPayload);
            await verifyRegisteredPolicy(normalizedPayload);
            if (!isCurrentConsentRequest(requestKey, requestGeneration) || isWithdrawn(persistedPayload.participant_id)) {
                return { transport: "superseded", receipt_verified: false, error: "Consent recording was superseded by a later choice" };
            }
            const result = await sendToCentral("consents", normalizedPayload, key, "POST", participantToken);
            if (!CONSENT_ID_PATTERN.test(String(result.data?.consent_id || ""))) {
                throw new Error("Central collection did not return a valid consent receipt");
            }
            if (!isCurrentConsentRequest(requestKey, requestGeneration) || isWithdrawn(persistedPayload.participant_id)) {
                return { transport: "superseded", receipt_verified: false, error: "Consent recording was superseded by a later choice" };
            }
            persistConsentReceipt(normalizedPayload, result.data);
            clearPendingConsent(persistedPayload.participant_id, appId);
            return { ...result, receipt_verified: true };
        } catch (error) {
            if (isCurrentConsentRequest(requestKey, requestGeneration)
                && (error.collectionStatus === 410 || error.causes?.some(cause => cause.collectionStatus === 410))) {
                // Recover a server withdrawal whose local marker/receipt was lost.
                // Preserve the original credential for follow-up, never reactivate it.
                const participantId = persistedPayload.participant_id;
                const receipt = { participant_id: participantId, status: "withdrawn", server_confirmed: true };
                const withdrawn = readObject(WITHDRAWN_STORAGE_KEY);
                withdrawn[participantId] = receipt;
                writeObject(WITHDRAWN_STORAGE_KEY, withdrawn);
                const deletions = readObject(DELETION_STORAGE_KEY);
                deletions[participantId] = receipt;
                writeObject(DELETION_STORAGE_KEY, deletions);
                clearParticipantConsentState(participantId, appId);
                return { transport: "withdrawn", receipt_verified: false, reenrollment_required: true,
                    error: "The previous enrollment was withdrawn. Consent again to start a new enrollment for future contributions only; old records remain withdrawn." };
            }
            if (isCurrentConsentRequest(requestKey, requestGeneration)) {
                persistPendingConsent(persistedPayload, key, error.message);
            }
            return { transport: "pending", receipt_verified: false, error: error.message };
        } finally {
            if (consentRequests.get(requestKey) === request) consentRequests.delete(requestKey);
        }
    })();
    consentRequests.set(requestKey, request);
    return request;
};

export const requireCollectionConsent = async ({
    participant_id: participantId,
    app_id: appId = DEFAULT_APP_ID,
    consent_version: consentVersion = COLLECTION_CONSENT_VERSION,
    modality,
}) => {
    if (withdrawalRequests.has(participantId)) throw new Error("Data collection is paused while withdrawal is being requested");
    if (!PARTICIPANT_ID_PATTERN.test(String(participantId || ""))) {
        throw new Error("A secure pseudonymous participant ID is required before contributing data");
    }
    if (isWithdrawn(participantId)) {
        throw new Error("Data collection is disabled because this participant requested withdrawal/deletion");
    }
    const requestKey = consentRequestKey(participantId, appId);
    if (consentRequests.has(requestKey)) await consentRequests.get(requestKey);

    const receipt = getConsentReceipt(participantId, appId);
    if (!isVerifiedReceipt(receipt, { participantId, appId, consentVersion })) {
        throw new Error("A durable server-issued consent receipt is required before contributing data");
    }
    if (!getParticipantToken(participantId, appId)) {
        throw new Error("The client-held participant credential is unavailable; consent again before contributing data");
    }
    if (modality && !receipt.permitted_modalities.includes(modality)) {
        throw new Error(`Your saved consent does not permit ${modality} contributions`);
    }
    return receipt;
};

export const submitCollectionSubmission = async (payload, { idempotencyKey } = {}) => {
    const inputModality = payload.input_modality === "voice" ? "speech" : payload.input_modality;
    const appId = payload.app_id || DEFAULT_APP_ID;
    const receipt = await requireCollectionConsent({
        participant_id: payload.participant_id,
        app_id: appId,
        consent_version: payload.consent_version || COLLECTION_CONSENT_VERSION,
        modality: inputModality,
    });
    const taskId = payload.task_id || createId("task");
    const normalizedPayload = pickFields({
        ...payload,
        task_id: taskId,
        consent_id: receipt.consent_id,
        input_modality: inputModality,
        started_at: payload.started_at || undefined,
        duration_ms: Number.isFinite(payload.duration_ms) ? payload.duration_ms : undefined,
    }, SUBMISSION_FIELDS);
    return sendToCentral(
        "submissions", normalizedPayload, idempotencyKey || taskId, "POST",
        getParticipantToken(payload.participant_id, appId),
    );
};

export const submitJudgmentEvent = async (payload) => {
    if (!SUBMISSION_ID_PATTERN.test(String(payload.submission_id || ""))) {
        throw new Error("This review task is not linked to a consented v2 submission");
    }
    const appId = payload.app_id || DEFAULT_APP_ID;
    const receipt = await requireCollectionConsent({
        participant_id: payload.participant_id,
        app_id: appId,
        consent_version: payload.consent_version || COLLECTION_CONSENT_VERSION,
        modality: "judgment",
    });
    const idempotencyKey = payload.idempotency_key || createId("judgment");
    const normalizedPayload = pickFields({ ...payload, consent_id: receipt.consent_id }, JUDGMENT_FIELDS);
    return sendToCentral(
        "judgments", normalizedPayload, idempotencyKey, "POST",
        getParticipantToken(payload.participant_id, appId),
    );
};

const clearParticipantConsentState = (participantId, appId) => {
    const requestKey = consentRequestKey(participantId, appId);
    advanceConsentRequestGeneration(requestKey);
    const receipts = readObject(CONSENT_STORAGE_KEY);
    const pending = readObject(PENDING_CONSENT_KEY);
    delete receipts[requestKey];
    delete pending[requestKey];
    writeObject(CONSENT_STORAGE_KEY, receipts);
    writeObject(PENDING_CONSENT_KEY, pending);
};

export const getCollectionWithdrawalReceipt = (participantId) => {
    if (!participantId) return null;
    const receipt = readObject(DELETION_STORAGE_KEY)[participantId];
    return receipt ? { participant_id: participantId, ...receipt } : null;
};

export const disableCollectionContributions = (participantId, appId = DEFAULT_APP_ID) => {
    clearParticipantConsentState(participantId, appId);
};

const rotateCollectionParticipantId = (participantNamespace) => {
    const nextParticipantId = createSecureParticipantId();
    if (!nextParticipantId) return "";
    try {
        storage()?.setItem(`pure_collection_participant_${participantNamespace}_v2`, nextParticipantId);
    } catch { /* The next page load will create another pseudonymous ID. */ }
    return nextParticipantId;
};

export const requestParticipantDeletion = async (
    participantId,
    { appId = DEFAULT_APP_ID, participantNamespace = DEFAULT_PARTICIPANT_NAMESPACE, reason = "consent_withdrawal" } = {},
) => {
    if (withdrawalRequests.has(participantId)) throw new Error("A withdrawal request is already in progress");
    const idempotencyKey = createId("deletion");
    const resource = `participants/${encodeURIComponent(participantId)}/data`;
    const participantToken = getParticipantToken(participantId, appId);
    advanceConsentRequestGeneration(consentRequestKey(participantId, appId));
    withdrawalRequests.add(participantId);
    try {
        const result = await sendToCentral(
            resource, { reason, scope: "all" }, idempotencyKey, "DELETE", participantToken,
        );
        if (!result.data?.deletion_request_id) {
            throw new Error("The server did not return a tracked deletion request");
        }
        clearParticipantConsentState(participantId, appId);
        const withdrawn = readObject(WITHDRAWN_STORAGE_KEY);
        withdrawn[participantId] = {
            deletion_request_id: result.data.deletion_request_id,
            requested_at: result.data.requested_at,
            status: result.data.status,
        };
        writeObject(WITHDRAWN_STORAGE_KEY, withdrawn);
        const deletions = readObject(DELETION_STORAGE_KEY);
        deletions[participantId] = result.data;
        writeObject(DELETION_STORAGE_KEY, deletions);
        // Keep the credential for status checks and retries while propagation is pending.
        return { ...result, rotated_participant_id: "" };
    } finally { withdrawalRequests.delete(participantId); }
};

export const getPrivateAudioContext = () => {
    const participant_id = getCollectionParticipantId(null, DEFAULT_PARTICIPANT_NAMESPACE);
    if (storage()?.getItem("pureversation_consent_mode") === "browse-only" || !hasVerifiedCollectionConsent(participant_id, DEFAULT_APP_ID, "speech")) return null;
    return { participant_id, consent_id: getCollectionConsentId(participant_id, DEFAULT_APP_ID), app_id: DEFAULT_APP_ID };
};
export const requestPrivateAudio = async (resource, options = {}) => {
    if (!/^(policy|consents|sessions|recordings)(\/[a-z]+_[0-9a-f]{32})?$/.test(resource)) throw new Error("Invalid recording resource");
    const method = options.method || "GET";
    const publicRequest = options.publicRequest === true && resource === "policy" && method === "GET";
    const participantId = getCollectionParticipantId(null, DEFAULT_PARTICIPANT_NAMESPACE);
    const participantToken = publicRequest ? "" : getParticipantToken(participantId, DEFAULT_APP_ID);
    if (!publicRequest && !participantToken) throw new Error("Your participant credential is required for this operation");
    if (method === "POST" && !getPrivateAudioContext()) throw new Error("Verified speech research consent is required");
    const controller = new AbortController();
    const cancel = () => controller.abort();
    options.signal?.addEventListener("abort", cancel, { once: true });
    if (options.signal?.aborted) cancel();
    const timeout = setTimeout(cancel, 30000);
    try {
        const response = await fetch(COLLECTION_API_ORIGIN + "/api/v2/raw-audio/" + resource, {
            method, body: options.body, signal: controller.signal, keepalive: options.keepalive === true,
            headers: { ...options.headers, ...(publicRequest ? {} : {
                "X-Participant-ID": participantId, "X-Participant-Token": participantToken,
                "Idempotency-Key": options.idempotencyKey || "audio_" + globalThis.crypto.randomUUID(),
            }) },
        });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.detail === "string" ? result.detail : "Recording request was rejected");
        return result;
    } finally { clearTimeout(timeout); options.signal?.removeEventListener("abort", cancel); }
};
